import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Shield,
  Plus,
  Users,
  Settings,
  Edit,
  Trash2,
  Copy,
  Save,
  X,
  Check,
  ChevronRight,
  Lock,
  Unlock,
  FileText,
  ShoppingCart,
  Mail,
  BarChart2,
  UserCog,
  BookOpen,
  Target,
  Loader2,
  FolderOpen,
} from 'lucide-react';
import api from '@/lib/api';

const MODULE_ICONS = {
  clients: Users,
  devis: FileText,
  catalogue: ShoppingCart,
  users: UserCog,
  roles: Shield,
  emailing: Mail,
  blog: BookOpen,
  analytics: BarChart2,
  settings: Settings,
  parcours: Target,
  mediatheque: FolderOpen,
};

const RolesPermissionsPage = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form states
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleLabel, setNewRoleLabel] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesRes, modulesRes] = await Promise.all([
        api.get('/roles'),
        api.get('/roles/modules'),
      ]);
      setRoles(rolesRes.data.roles);
      setModules(modulesRes.data.modules);
      
      // Sélectionner le premier rôle par défaut
      if (rolesRes.data.roles.length > 0 && !selectedRole) {
        setSelectedRole(rolesRes.data.roles[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des rôles');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRole = (role) => {
    if (editMode && hasChanges()) {
      if (!window.confirm('Vous avez des modifications non sauvegardées. Voulez-vous les perdre ?')) {
        return;
      }
    }
    setSelectedRole(role);
    setEditMode(false);
    setEditedPermissions({});
  };

  const handleEditMode = () => {
    setEditMode(true);
    setEditedPermissions(JSON.parse(JSON.stringify(selectedRole.permissions)));
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedPermissions({});
  };

  const handlePermissionChange = (module, action, value) => {
    setEditedPermissions(prev => ({
      ...prev,
      [module]: {
        ...(prev[module] || {}),
        [action]: value,
      }
    }));
  };

  const handleSelectAllModule = (module, value) => {
    const actions = Object.keys(modules[module]?.actions || {});
    const newPerms = {};
    actions.forEach(action => {
      newPerms[action] = value;
    });
    setEditedPermissions(prev => ({
      ...prev,
      [module]: newPerms,
    }));
  };

  const hasChanges = () => {
    return JSON.stringify(editedPermissions) !== JSON.stringify(selectedRole?.permissions);
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    
    setSaving(true);
    try {
      await api.put(`/roles/${selectedRole.id || selectedRole.name}`, {
        permissions: editedPermissions,
      });
      
      toast.success('Permissions mises à jour');
      setEditMode(false);
      
      // Mettre à jour le rôle localement
      setSelectedRole(prev => ({ ...prev, permissions: editedPermissions }));
      setRoles(prev => prev.map(r => 
        (r.id || r.name) === (selectedRole.id || selectedRole.name) 
          ? { ...r, permissions: editedPermissions }
          : r
      ));
      setEditedPermissions({});
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim() || !newRoleLabel.trim()) {
      toast.error('Veuillez remplir le nom et le libellé');
      return;
    }

    try {
      const response = await api.post('/roles', {
        name: newRoleName.toLowerCase().replace(/\s+/g, '_'),
        label: newRoleLabel,
        description: newRoleDescription,
        permissions: {},
      });
      
      toast.success('Rôle créé avec succès');
      setCreateDialogOpen(false);
      setNewRoleName('');
      setNewRoleLabel('');
      setNewRoleDescription('');
      
      // Ajouter le nouveau rôle et le sélectionner
      const newRole = response.data.role;
      setRoles(prev => [...prev, newRole]);
      setSelectedRole(newRole);
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDuplicateRole = async () => {
    if (!selectedRole || !newRoleName.trim() || !newRoleLabel.trim()) {
      toast.error('Veuillez remplir le nom et le libellé');
      return;
    }

    try {
      const response = await api.post(`/roles/${selectedRole.id || selectedRole.name}/duplicate`, null, {
        params: {
          new_name: newRoleName,
          new_label: newRoleLabel,
        }
      });
      
      toast.success('Rôle dupliqué avec succès');
      setDuplicateDialogOpen(false);
      setNewRoleName('');
      setNewRoleLabel('');
      
      // Ajouter le nouveau rôle et le sélectionner
      const newRole = response.data.role;
      setRoles(prev => [...prev, newRole]);
      setSelectedRole(newRole);
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la duplication');
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRole || selectedRole.is_system) return;

    try {
      await api.delete(`/roles/${selectedRole.id}`);
      toast.success('Rôle supprimé');
      setDeleteDialogOpen(false);
      
      // Retirer le rôle et sélectionner le premier
      setRoles(prev => prev.filter(r => r.id !== selectedRole.id));
      setSelectedRole(roles.find(r => r.id !== selectedRole.id) || null);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getModulePermissions = (module) => {
    if (editMode) {
      return editedPermissions[module] || selectedRole?.permissions?.[module] || {};
    }
    return selectedRole?.permissions?.[module] || {};
  };

  const isModuleFullyEnabled = (module) => {
    const perms = getModulePermissions(module);
    const actions = Object.keys(modules[module]?.actions || {});
    return actions.length > 0 && actions.every(action => perms[action] === true);
  };

  const countEnabledActions = (module) => {
    const perms = getModulePermissions(module);
    return Object.values(perms).filter(v => v === true).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roles-permissions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Rôles & Permissions
          </h1>
          <p className="text-slate-500 mt-1">
            Gérez les types d'utilisateurs et leurs droits d'accès
          </p>
        </div>
        <Button 
          onClick={() => setCreateDialogOpen(true)}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="create-role-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau rôle
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Roles List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Types d'utilisateurs
            </CardTitle>
            <CardDescription>
              {roles.length} rôle(s) configuré(s)
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {roles.map((role) => (
                <div
                  key={role.id || role.name}
                  className={`p-4 cursor-pointer transition-colors hover:bg-slate-50 ${
                    selectedRole?.name === role.name ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''
                  }`}
                  onClick={() => handleSelectRole(role)}
                  data-testid={`role-item-${role.name}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{role.label}</span>
                        {role.is_system && (
                          <Badge variant="outline" className="text-xs bg-slate-100">
                            <Lock className="w-3 h-3 mr-1" />
                            Système
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-1">
                        {role.description || `Rôle ${role.name}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-600">
                        <Users className="w-3 h-3 mr-1" />
                        {role.user_count}
                      </Badge>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${
                        selectedRole?.name === role.name ? 'rotate-90' : ''
                      }`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Permissions Editor */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  {selectedRole ? (
                    <>
                      <Settings className="w-5 h-5" />
                      Permissions : {selectedRole.label}
                    </>
                  ) : (
                    'Sélectionnez un rôle'
                  )}
                </CardTitle>
                {selectedRole && (
                  <CardDescription>{selectedRole.description}</CardDescription>
                )}
              </div>
              {selectedRole && (
                <div className="flex items-center gap-2">
                  {editMode ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEdit}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Annuler
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSavePermissions}
                        disabled={saving || !hasChanges()}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-1" />
                        )}
                        Enregistrer
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setNewRoleName('');
                          setNewRoleLabel('');
                          setDuplicateDialogOpen(true);
                        }}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Dupliquer
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditMode}
                        data-testid="edit-permissions-btn"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                      {!selectedRole.is_system && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteDialogOpen(true)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {selectedRole ? (
              <Accordion type="multiple" defaultValue={Object.keys(modules)} className="space-y-2">
                {Object.entries(modules).map(([moduleKey, moduleData]) => {
                  const IconComponent = MODULE_ICONS[moduleKey] || Settings;
                  const perms = getModulePermissions(moduleKey);
                  const enabledCount = countEnabledActions(moduleKey);
                  const totalActions = Object.keys(moduleData.actions).length;
                  const isFullyEnabled = isModuleFullyEnabled(moduleKey);

                  return (
                    <AccordionItem 
                      key={moduleKey} 
                      value={moduleKey}
                      className="border rounded-lg px-4"
                    >
                      <AccordionTrigger className="hover:no-underline py-3">
                        <div className="flex items-center justify-between w-full pr-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${
                              isFullyEnabled ? 'bg-green-100 text-green-600' : 
                              enabledCount > 0 ? 'bg-orange-100 text-orange-600' : 
                              'bg-slate-100 text-slate-400'
                            }`}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-slate-800">{moduleData.label}</p>
                              <p className="text-xs text-slate-500">{moduleData.description}</p>
                            </div>
                          </div>
                          <Badge className={`${
                            isFullyEnabled ? 'bg-green-100 text-green-700' :
                            enabledCount > 0 ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-500'
                          }`}>
                            {enabledCount} / {totalActions}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-4">
                        {editMode && (
                          <div className="mb-3 pb-3 border-b">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={isFullyEnabled}
                                onCheckedChange={(checked) => handleSelectAllModule(moduleKey, checked)}
                              />
                              <span className="text-sm font-medium text-slate-700">
                                Tout sélectionner
                              </span>
                            </label>
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {Object.entries(moduleData.actions).map(([actionKey, actionLabel]) => (
                            <label
                              key={actionKey}
                              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                editMode ? 'cursor-pointer hover:bg-slate-50' : ''
                              } ${
                                perms[actionKey] ? 'border-green-200 bg-green-50' : 'border-slate-200'
                              }`}
                            >
                              <Checkbox
                                checked={perms[actionKey] === true}
                                onCheckedChange={(checked) => 
                                  editMode && handlePermissionChange(moduleKey, actionKey, checked)
                                }
                                disabled={!editMode}
                              />
                              <div className="flex-1">
                                <p className={`text-sm font-medium ${
                                  perms[actionKey] ? 'text-green-700' : 'text-slate-600'
                                }`}>
                                  {actionLabel}
                                </p>
                              </div>
                              {perms[actionKey] ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <X className="w-4 h-4 text-slate-300" />
                              )}
                            </label>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-20" />
                <p>Sélectionnez un rôle pour voir ses permissions</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Role Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un nouveau rôle</DialogTitle>
            <DialogDescription>
              Définissez un nouveau type d'utilisateur avec des permissions personnalisées
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="role-name">Nom technique</Label>
              <Input
                id="role-name"
                placeholder="ex: commercial_senior"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
              />
              <p className="text-xs text-slate-500">
                Utilisé en interne, sans espaces ni caractères spéciaux
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role-label">Libellé affiché</Label>
              <Input
                id="role-label"
                placeholder="ex: Commercial Senior"
                value={newRoleLabel}
                onChange={(e) => setNewRoleLabel(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role-desc">Description (optionnelle)</Label>
              <Textarea
                id="role-desc"
                placeholder="Décrivez ce rôle..."
                value={newRoleDescription}
                onChange={(e) => setNewRoleDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateRole} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="w-4 h-4 mr-2" />
              Créer le rôle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Role Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dupliquer le rôle "{selectedRole?.label}"</DialogTitle>
            <DialogDescription>
              Créez un nouveau rôle basé sur les permissions de {selectedRole?.label}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dup-name">Nom technique du nouveau rôle</Label>
              <Input
                id="dup-name"
                placeholder="ex: commercial_junior"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dup-label">Libellé affiché</Label>
              <Input
                id="dup-label"
                placeholder="ex: Commercial Junior"
                value={newRoleLabel}
                onChange={(e) => setNewRoleLabel(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleDuplicateRole} className="bg-orange-500 hover:bg-orange-600">
              <Copy className="w-4 h-4 mr-2" />
              Dupliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer le rôle</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer le rôle "{selectedRole?.label}" ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          {selectedRole?.user_count > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              ⚠️ Ce rôle est actuellement attribué à {selectedRole.user_count} utilisateur(s).
              Vous devez d'abord les réassigner à un autre rôle.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRole}
              disabled={selectedRole?.user_count > 0}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPermissionsPage;
